import { useGlassmorphism } from '@/hooks/useGlassmorphism';
import { cn } from '@/utils/cn';
import React from 'react';

interface GlassLayoutProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * GlassLayout - Layout principale con background glass
 * Desktop: blob animati
 * PWA: gradienti statici
 */
export function GlassLayout({ children, className }: GlassLayoutProps) {
  const { mode } = useGlassmorphism();

  return (
    <div className={cn('h-full flex flex-col relative', className)}>
      <GlassBackground mode={mode} />
      <div className="relative z-10 flex-1 flex flex-col min-h-0">
        {children}
      </div>
    </div>
  );
}

/**
 * Background differenziato per Desktop vs PWA
 */
function GlassBackground({ mode }: { mode: 'desktop' | 'pwa' | 'none' }) {
  if (mode === 'desktop') {
    return <DesktopBackground />;
  }
  if (mode === 'pwa') {
    return <PWABackground />;
  }
  return null;
}

/**
 * Desktop: Blob animati (GPU powered)
 * USA CSS variables dal configuratore - INCLUSO IL GRADIENTE DEL TEMA
 */
function DesktopBackground() {
  return (
    <>
      {/* Base gradient from theme - this is what changes with presets! */}
      {/* Use transparent fallback when glass is not enabled */}
      <div 
        className="fixed inset-0 pointer-events-none -z-10"
        style={{ background: 'var(--glass-bg-gradient, transparent)' }}
      />
      {/* Blob 1 - Gray neutral fallback - reduced motion handled via CSS */}
      <div
        className="fixed -top-1/4 -left-1/4 w-[800px] h-[800px] 
                   rounded-full blur-[120px] animate-blob pointer-events-none -z-10"
        style={{ 
          backgroundColor: 'var(--color-primary, #242424)', 
          opacity: 0.3,
        }}
      />
      {/* Blob 2 - Gray accent fallback */}
      <div
        className="fixed -bottom-1/4 -right-1/4 w-[600px] h-[600px] 
                   rounded-full blur-[100px] animate-blob pointer-events-none -z-10"
        style={{ 
          backgroundColor: 'var(--color-accent, #0099ff)', 
          opacity: 0.25,
          animationDelay: '2s' 
        }}
      />
      {/* Blob 3 - Gray secondary fallback */}
      <div
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 
                   w-[400px] h-[400px] rounded-full 
                   blur-[80px] animate-blob pointer-events-none -z-10"
        style={{ 
          backgroundColor: 'var(--color-secondary, #0099ff)', 
          opacity: 0.2,
          animationDelay: '1s' 
        }}
      />
    </>
  );
}

/**
 * PWA: Gradienti statici (risparmio batteria)
 * USA CSS variables dal configuratore - INCLUSO IL GRADIENTE DEL TEMA
 */
function PWABackground() {
  return (
    <>
      {/* Base gradient from theme - this is what changes with presets! */}
      {/* Use transparent fallback when glass is not enabled */}
      <div 
        className="fixed inset-0 pointer-events-none -z-10"
        style={{ background: 'var(--glass-bg-gradient, transparent)' }}
      />
      {/* Top blob */}
      <div 
        className="fixed top-0 left-0 w-full h-1/2 
                       rounded-b-[50%] blur-[60px] 
                       pointer-events-none -z-10" 
        style={{ backgroundColor: 'var(--color-primary)', opacity: 0.2 }}
      />
      {/* Bottom blob */}
      <div 
        className="fixed bottom-0 right-0 w-full h-1/2 
                       rounded-t-[50%] blur-[60px] 
                       pointer-events-none -z-10" 
        style={{ backgroundColor: 'var(--color-accent)', opacity: 0.15 }}
      />
    </>
  );
}

export default GlassLayout;
